// ─────────────────────────────────────────────────────────────────────────────
// i18n/en.ts
// ─────────────────────────────────────────────────────────────────────────────

// Locale interface — uses `string` so translations can supply any value
export interface Locale {
  realEstateInsights: { title: string; hint: string; structure: string; parcel: string; sun: string; neighborhood: string; outlook: string; privacy: string; terrain: string };
  sunPath: { title: string; date: string; localTime: string; sunrise: string; sunset: string;
    play: string; pause: string; close: string; today: string; summer: string; winter: string;
    elevation: string; direction: string; notice: string; unavailable: string; west: string; fit: string;
    lowSun: string; midSun: string; highSun: string };
  pins: {
    assets: string; loading: string; failed: string; retry: string; visible: string;
    size: string; opacity: string; colorBy: string; uniform: string; all: string;
    reset: string; view: string; selected: string; close: string; count: (shown: number, total: number) => string;
    load: string; invalid: string; selectHint: string;
  };
  viewer: {
    errorTitle:        string;
    errorBack:         string;
    errorMissingScene: string;
    errorNoManifest:   (name: string) => string;
    errorNotFound:     (id: string)   => string;
    errorInit:         string;
    errorLoad3D:       string;
    loading3D:         string;
    preparingModel:    string;
    startingViewer:    string;
    downloadingModel:  (pct: number) => string;
    processingModel:   string;
    loadingModelLabel: (label: string) => string;
    assetsBtn:         string;
    sourceImagesBtn:   string;
    measureBtn:        string;
    selectionBtn:      string;
    selectionBtnTitle: string;
    imageViewBtn:      string;
    imageViewTitle:    string;
    imageViewLoading:  string;
    segmentLoading:    string;
    poweredBy:         string;
    navTitle:          string;
    navLookAround:     string;
    navLookHint:       string;
    navMove:           string;
    navMoveHint:       string;
    navAdvanced:       string;
    navUp:             string;
    navDown:           string;
  };
  measure: {
    modePoint:           string;
    modeLine:            string;
    modePolygon:         string;
    instructClickFirst:  string;
    instructPointPlaced: string;
    instructAddMore:     (n: number) => string;
    clickToPlace:        string;
    statLength:          string;
    statCoordinates:     string;
    statPerimeter:       string;
    statArea:            string;
    statSegmentSlopes:   string;
    statNoElevation:     string;
    slopeAngleSlope:     string;
    slopeAngleVertical:  string;
    slopeAngleBase:      string;
    slopeRun:            string;
    slopeRise:           string;
    slopeGrade:          string;
    slopePointC:         string;
    btnCompletePolygon:  string;
    btnAnalyzeSlope:     string;
    btnAnalyzeHeight:    string;
    btnAnalyzeWidth:     string;
    btnClearAll:         string;
    btnDone:             string;
    modeWidth:           string;
    statSlope:           string;
    statPerpWidth:       string;
    widthGuideHint:      string;
    widthPickedLineNote: string;
    widthDescription:    string;
    widthComputeError:   string;
  };
  poiNav: {
    navigatingTo:  string;
    loadingScene:  string;
    selectedPoint: string;
  };
  poiNotif: {
    veryClose: string;
    nearby:    string;
    ahead:     string;
    goBtn:     string;
  };
  sourceImages: {
    zoomReset:          string;
    scrollToZoom:       string;
    scrollZoomDragPan:  string;
    seedQuestion:       string;
    seedHint:           string;
    pickDifferentPoint: string;
    close:              string;
    prev:               string;
    selectThisView:     string;
    more:               string;
    moreLeft:           (n: number) => string;
    loadingImages:      string;
    backToViews:        string;
    clickToZoom:        string;
    finding:            string;
    clickInstruction:   string;
  };
  viewerAgent: {
    phLoading:            string;
    phPlanning:           string;
    phAsk:                string;
    cancelled:            string;
    waitingFromStatus:    (status: string) => string;
    waitingBroadAnalysis: string;
    stillWorking:         string;
    takingLonger:         string;
    spatialAnalysis:      string;
    spatialAnalysisFiles: (n: number) => string;
    handingOff:           string;
    connectingSI:         string;
    siError:              (msg: string) => string;
    connStoppedWithText:  string;
    connStoppedNoText:    string;
    preview:              string;
    download:             string;
    pendingResultsReady:  string;
    checkNow:             string;
    checkResultsPrompt:   (q: string) => string;
    dismiss:              string;
    outputFile:           string;
    welcomeQuestion:      string;
    runningTool:          (name: string) => string;
    siTurnComplete:       string;
    siAnalysisComplete:   string;
    hideHistory:          string;
    viewHistory:          string;
    openInViewer:         string;
    detectedFeatures:     string;
    flyingTo:             (label: string) => string;
    openChat:             string;
    viewIn3D:             string;
    cancelAriaLabel:      string;
    sendAriaLabel:        string;
    toggleLangAriaLabel:  string;
    minimizeChat:         string;
    selectionDoneGeneric: string;
    thinkingPhrases:      string[];
    paddingStages:        string[];
  };
  artifact: {
    openTab:              string;
    notBrowserReadable:   string;
    filesystemPathNote:   string;
  };
  sceneLabels: {
    hiddenOne:   (text: string) => string;
    hiddenMany:  (count: number) => string;
  };
  reconstructionAgent: {
    // Chrome
    title:            string;
    openChat:         string;
    minimize:         string;
    toggleLang:       string;
    restartSession:   string;
    expandChat:       string;
    collapseChat:     string;
    send:             string;
    placeholder:      string;

    // Opening choice
    welcome:           string;
    selectToProceed:   string;
    optionOwnData:     string;
    optionOwnDataHint: string;
    optionOwnDataMsg:  string;
    optionAddress:     string;
    optionAddressHint: string;
    optionAddressMsg:  string;

    // Own-capture path
    uploadIntro:    string;
    uploadHosts:    string;
    uploadPick:     string;
    uploadProgress: (done: number, total: number) => string;
    uploadDone:     (count: number) => string;
    uploadFailed:   string;
    uploadClear:    string;

    // Address path
    addressIntro:       string;
    addressPlaceholder: string;
    extentIntro:        string;
    extentCheckCoverage: string;
    coverageDecline:    string;
    previewVerifying:   string;
    previewReadyMsg:    string;
    previewUnsuitable:  string;
    previewExpired:     string;
    previewForbidden:   string;
    newRequestDivider:  string;
    namePlaceholder:    string;
    companyPlaceholder: string;
    emailRequiredMark:  string;
    emailWhyCancel:     string;
    cancelEmailNoEmail: string;
    cancelEmailTrying:  string;
    cancelEmailOther:   string;
    previewWhyCancel:   string;
    previewCancelThanks: string;
    previewStart:       string;
    cancelled:          string;
    cancelledNoEmail:   string;
    cancelReasonQuality: string;
    cancelReasonCoverage: string;
    cancelReasonOther:  string;
    emailPlaceholder:   string;
    emailStart:         string;
    emailRequired:      string;
    emailInvalid:       string;
    coverageEmailAsk:   string;
    extentStart:        string;
    extentChecking:     string;
    cancel:             string;

    // Dataset warning
    warningProceed: string;
    warningRevise:  string;
    warningWorking: string;

    // Progress and outcome
    locatingAddress:     string;
    systemChecking:      string;
    checkingPlaceholder: string;
    awaitingConfirm:     string;
    statusChecking:      string;
    statusProcessing:    string;
    statusDone:          string;
    doneTitle:           string;
    doneNote:            string;
    doneNoteWorldMap:    string;
    startOver:          string;
    extentSelected:     string;
    previewAccepted:    string;
    filesGenerated:      string;
    errorRetry:          string;
  };
}

// English strings — satisfies Locale
const en: Locale = {
  realEstateInsights: { title: 'Property insights', hint: 'Explore the home and its surroundings',
    structure: 'Roof, home details, tree cover and hard surfaces', parcel: 'View boundaries, dimensions and lot area',
    sun: 'Follow the sun through the day and seasons', neighborhood: 'Compare nearby homes, trees and potential view obstructions',
    privacy: 'See which exterior surfaces face neighboring windows',
    outlook: 'Explore nearby trees and potential view obstructions', terrain: 'Explore the elevation heatmap and yard slope' },
  sunPath: { title: 'Sun path', date: 'Date', localTime: 'Medical Lake time · Pacific', sunrise: 'Sunrise', sunset: 'Sunset',
    play: 'Play day', pause: 'Pause', close: 'Close sun path', today: 'Today', summer: 'Summer', winter: 'Winter',
    elevation: 'Sun elevation', direction: 'Direction', notice: 'Illustrative sunlight · Shadows and weather are not simulated.',
    unavailable: 'Sunrise or sunset is unavailable for this date.', west: 'W', fit: 'Fit sun path in view',
    lowSun: 'Low-angle sun · warm and soft', midSun: 'Angled sun · gentle daylight', highSun: 'High sun · brighter daylight' },
  pins: {
    assets: 'Pin assets', loading: 'Loading pins…', failed: 'Could not load pins.', retry: 'Retry',
    visible: 'Show markers', size: 'Marker size', opacity: 'Opacity', colorBy: 'Color by',
    uniform: 'Single color', all: 'All', reset: 'Clear filters', view: 'Go to saved view',
    selected: 'Selected pin', close: 'Collapse pin details', count: (shown, total) => `${shown.toLocaleString()} / ${total.toLocaleString()} pins`,
    load: 'Display pins', invalid: 'The pins file is invalid or unsupported.', selectHint: 'Click a marker to inspect it.',
  },
  viewer: {
    errorTitle:          'Error Loading Scene',
    errorBack:           'Go Back',
    errorMissingScene:   'Missing required URL parameter: scene',
    errorNoManifest:     (name) => `Scene '${name}' does not have a manifest URL`,
    errorNotFound:       (id)   => `No scene found for scene ID: ${id}`,
    errorInit:           'Failed to initialize viewer',
    errorLoad3D:         'Failed to load 3D scene',
    loading3D:           'Loading 3D model...',
    preparingModel:      'Preparing 3D model...',
    startingViewer:      'Starting 3D viewer...',
    downloadingModel:    (pct) => `Downloading 3D model: ${pct}%`,
    processingModel:     'Processing 3D model...',
    loadingModelLabel:   (label: string) => `Loading 3D model: ${label}`,
    assetsBtn:           'Assets',
    sourceImagesBtn:     'Source Images',
    measureBtn:          'Measurement',
    selectionBtn:        'Select',
    selectionBtnTitle:   'Select context for query',
    imageViewBtn:        'Image View',
    imageViewTitle:      'Switch to Image View',
    imageViewLoading:    'Loading 3D model…',
    segmentLoading:      'Segment loading…',
    poweredBy:           'Powered by',
    navTitle:            'Navigation',
    navLookAround:       'Look around',
    navLookHint:         'Click & drag',
    navMove:             'Move',
    navMoveHint:         'Arrow keys',
    navAdvanced:         'Advanced navigation:',
    navUp:               'Up',
    navDown:             'Down',
  },
  measure: {
    modePoint:           'Point',
    modeLine:            'Line',
    modePolygon:         'Polygon',
    instructClickFirst:  'Click first point in the scene',
    instructPointPlaced: 'Point placed — click Done or add more',
    instructAddMore:     (n) => `${n} points — click to add more`,
    clickToPlace:        'Click in the scene to place points',
    statLength:          'Length',
    statCoordinates:     'Coordinates',
    statPerimeter:       'Perimeter',
    statArea:            'Area',
    statSegmentSlopes:   'Segment Slopes',
    statNoElevation:     'No elevation change detected',
    slopeAngleSlope:     'slope',
    slopeAngleVertical:  'vertical',
    slopeAngleBase:      'base',
    slopeRun:            'Run',
    slopeRise:           'Rise',
    slopeGrade:          'Grade',
    slopePointC:         'Point C (ground below H)',
    btnCompletePolygon:  'Complete polygon',
    btnAnalyzeSlope:     '⊿ Analyze Slope',
    btnAnalyzeHeight:    'Analyze height',
    btnAnalyzeWidth:     'Analyze width',
    btnClearAll:         'Clear all',
    btnDone:             'Done',
    modeWidth:           'Width',
    statSlope:           'Slope',
    statPerpWidth:       'Perpendicular Width',
    widthGuideHint:      "Drag A' or A\" to align path",
    widthPickedLineNote: 'Picked line already represents perpendicular width.',
    widthDescription:    'Width is shortest distance from B to line through A',
    widthComputeError:   'Unable to compute width for current guide line.',
  },
  poiNav: {
    navigatingTo:  'Navigating to',
    loadingScene:  'Loading nearby scene data…',
    selectedPoint: 'Selected point',
  },
  poiNotif: {
    veryClose: 'VERY CLOSE',
    nearby:    'NEARBY',
    ahead:     'AHEAD',
    goBtn:     'Go',
  },
  sourceImages: {
    zoomReset:          'reset',
    scrollToZoom:       'scroll to zoom',
    scrollZoomDragPan:  'scroll to zoom · drag to pan',
    seedQuestion:       'Which view best captures your point?',
    seedHint:           "Pick the angle that matches — we'll find more from that perspective",
    pickDifferentPoint: 'Pick a different point',
    close:              'Close',
    prev:               'Prev',
    selectThisView:     'Select this view',
    more:               'More',
    moreLeft:           (n) => `${n} left`,
    loadingImages:      'Loading…',
    backToViews:        '← Back to views',
    clickToZoom:        'click thumbnail to zoom',
    finding:            'Finding best source images…',
    clickInstruction:   'Click the sign / object in the scene to find source images',
  },
  viewerAgent: {
    phLoading:            'Viewer is loading...',
    phPlanning:           'Planning...',
    phAsk:                'Ask the scene...',
    cancelled:            'Query stopped. What would you like to ask instead?',
    waitingFromStatus:    (status) => `Still waiting for Spatial Intelligence output from: ${status}`,
    waitingBroadAnalysis: 'Running a broad scene analysis — this can take a few minutes.',
    stillWorking:         'Processing the reconstruction data…',
    takingLonger:         'Running a spatial analysis — this may take a moment.',
    spatialAnalysis:      'Spatial analysis',
    spatialAnalysisFiles: (n) => `Spatial analysis · ${n} output file${n !== 1 ? 's' : ''}`,
    handingOff:           'Handing off to Spatial Intelligence...',
    connectingSI:         'Connecting to Spatial Intelligence',
    siError:              (msg) => `Spatial Intelligence error: ${msg}`,
    connStoppedWithText:  'The spatial analysis connection stopped before a final response. You can continue with a follow-up or retry the request.',
    connStoppedNoText:    'The spatial analysis connection stopped before a final response. Please try again or ask a narrower question.',
    preview:              'Preview',
    download:             'Download',
    pendingResultsReady:  'Your earlier request may have results ready:',
    checkNow:             'Yes, check now',
    checkResultsPrompt:   (q) => `Check if results are ready for my earlier request: ${q}`,
    dismiss:              'Dismiss',
    outputFile:           'Output file',
    welcomeQuestion:      'What can you do in this scene?',
    runningTool:          (name) => `Running: ${name}`,
    siTurnComplete:       'Spatial Intelligence turn complete.',
    siAnalysisComplete:   'Spatial Intelligence analysis complete.',
    hideHistory:          'Hide earlier messages',
    viewHistory:          'View earlier messages',
    openInViewer:         'Open in viewer',
    detectedFeatures:     'Detected features — drag corners to correct',
    flyingTo:             (label: string) => `Flying to ${label}`,
    openChat:             'Open chat',
    viewIn3D:             'View in 3D',
    cancelAriaLabel:      'Cancel',
    sendAriaLabel:        'Send',
    toggleLangAriaLabel:  'Toggle language',
    minimizeChat:         'Minimize chat',
    selectionDoneGeneric: 'I\'ve selected an area — what would you like to do here?',
    thinkingPhrases: [
      'Querying reconstruction data…',
      'Reading scene geometry…',
      'Checking stored scene knowledge…',
      'Running spatial analysis…',
      'Reviewing object layout…',
      'Preparing the answer…',
    ],
    paddingStages: [
      'Scanning reconstruction data…',
      'Locating assets in scene…',
      'Computing spatial coordinates…',
      'Cross-referencing knowledge base…',
      'Preparing response…',
    ],
  },
  artifact: {
    openTab:            'Open tab',
    notBrowserReadable: 'This output path is not browser-readable yet.',
    filesystemPathNote: 'The agent returned a filesystem path. The backend needs to expose this file as an HTTP URL before it can be previewed in the browser.',
  },
  sceneLabels: {
    hiddenOne:  (text: string) => `${text} label is outside the current view.`,
    hiddenMany: (count: number) => `${count} labels are outside the current view.`,
  },
  reconstructionAgent: {
    title:            'Reco',
    openChat:         'Open Reco',
    minimize:         'Minimize',
    toggleLang:       'Switch language',
    restartSession:   'Start over',
    expandChat:       'Open full page',
    collapseChat:     'Back to the site',
    send:             'Send',
    placeholder:      'Message Reco\u2026',

    welcome:          "I'm Reco. I help you reconstruct 3D spatial models "
                      + 'from your own photos or video.',
    selectToProceed:   'Get started',
    optionOwnData:     'My own capture',
    optionOwnDataHint: 'Photos or video you shot yourself',
    optionOwnDataMsg:  'I have my own images or video to upload.',
    optionAddress:     'A street address',
    optionAddressHint: 'We source the aerial imagery for you',
    optionAddressMsg:  'I want to reconstruct a location from an address.',

    uploadIntro:    'Share a link to your images.',
    uploadHosts:    'Drive, Dropbox, WeTransfer',
    uploadPick:     'Choose image folder',
    uploadProgress: (done, total) => `Uploading ${done} of ${total}\u2026`,
    uploadDone:     (count) => `${count} images uploaded`,
    uploadFailed:   'Upload failed',
    uploadClear:    'Remove upload',

    addressIntro:       'Enter the address you want to reconstruct.',
    addressPlaceholder: 'e.g. 600 Montgomery St, San Francisco',
    extentIntro:        'Select area to reconstruct',
    extentCheckCoverage: 'Check coverage',
    coverageDecline:    'Cancel',
    previewVerifying:   'We are doing a quick verification of the suitability of the aerial '
                      + 'images for 3D reconstruction. If it passes, you will be able to '
                      + 'preview a few sample images before approving the reconstruction.',
    previewReadyMsg:    'Verified! We have a set of sample images for you to review. If you '
                      + 'approve, I can proceed with 3D reconstruction.',
    previewUnsuitable:  'Unfortunately the images are not suitable for reconstruction. We will '
                      + 'work with our partner Vexcel to fix this issue.\n\n',
    previewExpired:     'The imagery preview has expired. Let us check coverage again so I '
                      + 'can show you fresh images.\n\n',
    previewForbidden:   'Unfortunately these images are not allowed for reconstructions yet. We '
                      + 'will work with our partner Vexcel to fix this issue.\n\n',
    newRequestDivider:  'New request',
    namePlaceholder:    'First and last name',
    companyPlaceholder: 'Company',
    emailRequiredMark:  'required',
    emailWhyCancel:     'No problem. What made you decide not to go ahead?',
    cancelEmailNoEmail: 'I prefer not to provide an email address',
    cancelEmailTrying:  'Just wanted to try out the chat experience',
    cancelEmailOther:   'Something else',
    previewWhyCancel:   'No problem. What made you decide not to go ahead?',
    previewCancelThanks: 'Thanks \u2014 that helps. Would you like to try another location, or use '
                      + 'your own capture?',
    previewStart:       'Looks good',
    cancelled:          'Cancelled',
    cancelledNoEmail:   'I don\u2019t want to provide an email. Cancel the reconstruction.',
    cancelReasonQuality: 'Image quality',
    cancelReasonCoverage: 'Insufficient coverage',
    cancelReasonOther:  'Something else',
    emailPlaceholder:   'you@company.com',
    emailStart:         'Start reconstruction',
    emailRequired:      'I need an email address before I can queue this.',
    emailInvalid:       'That doesn\u2019t look like an email address.',
    coverageEmailAsk:   "I'm ready to queue your reconstruction, which can take some time to "
                      + 'complete. To proceed I\u2019ll need an email address, so I can send you '
                      + 'progress and a link to the finished job.',
    extentStart:        'Start reconstruction',
    extentChecking:     'Checking imagery\u2026',
    cancel:             'Cancel',

    warningProceed: 'Continue anyway',
    warningRevise:  'Send a better dataset',
    warningWorking: 'Working\u2026',

    locatingAddress:     'Locating that address\u2026',
    systemChecking:      'Checking your dataset',
    checkingPlaceholder: 'Checking your dataset\u2026',
    awaitingConfirm:     'Confirm or cancel above\u2026',
    statusChecking:      'Checking',
    statusProcessing:    'Processing',
    statusDone:          'Received',
    doneTitle:           'Request received',
    doneNote:            "We're checking your dataset now. We'll email you when your model is ready.",
    doneNoteWorldMap:    'We\u2019re starting your reconstruction now. We\u2019ll email you when your model is ready.',
    startOver:          'Shall we start over?',
    extentSelected:     'I have selected the map area.',
    previewAccepted:    'Previews look good. Start the 3D reconstruction.',
    filesGenerated:      'Generated files',
    errorRetry:          'Could not reach Reco. Try again.',
  },
};

export default en;
